import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EuiButton,
  EuiCallOut,
  EuiFieldSearch,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPage,
  EuiPageBody,
  EuiPanel,
  EuiSelect,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { OplItem, OplItemType } from "src/questionnaires";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { TableSkeleton } from "src/components/skeleton";
import { useOplOverviewApi } from "./api";
import {
  DueFilter,
  DueFilterChips,
  dueCountsOf,
  matchesDue,
  OpenPointRow,
  OpenPointsTable,
  toOpenPointRows,
} from "./open-points";

const i18n = "anketa.opsanio.opl";

/**
 * Every OPEN OPL point across all appliances the viewer may read - what is
 * still outstanding, fleet-wide, most urgent first. A row opens the point on
 * its appliance's board.
 */
export const OpsanioOplOverviewPage = (): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { user } = useAnketaAuth();
  const navigate = useNavigate();
  const api = useOplOverviewApi();
  const t = useCallback(
    (k: string, d: string, values?: Record<string, unknown>) =>
      translate(`${i18n}.${k}`, { defaultMessage: d, values }),
    [translate],
  );

  const [items, setItems] = useState<OplItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [due, setDue] = useState<DueFilter>("all");
  const [type, setType] = useState<OplItemType | "all">("all");
  const [appliance, setAppliance] = useState("all");
  const [owner, setOwner] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);

  const myEmail = ((user as any)?.email ?? "").toLowerCase();

  const load = useCallback(async () => {
    if (!api.canCall) return;
    setLoading(true);
    setError(null);
    const result = await api.openItems();
    if (!result.ok) {
      setError(result.error ?? "Die offenen Punkte konnten nicht geladen werden.");
    } else {
      setItems(result.data ?? []);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => toOpenPointRows(items ?? []), [items]);
  const counts = useMemo(() => dueCountsOf(rows), [rows]);
  const appliances = useMemo(
    () => [...new Set(rows.map((r) => r.applianceName))].sort(),
    [rows],
  );
  const owners = useMemo(
    () => [...new Set(rows.map((r) => r.owner).filter(Boolean) as string[])].sort(),
    [rows],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!matchesDue(r, due)) return false;
      if (type !== "all" && r.type !== type) return false;
      if (appliance !== "all" && r.applianceName !== appliance) return false;
      if (owner !== "all" && r.owner !== owner) return false;
      if (mineOnly && (r.owner ?? "").toLowerCase() !== myEmail) return false;
      if (
        q !== "" &&
        ![r.key, r.title, r.applianceName, r.owner ?? "", r.lastLog?.text ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
        return false;
      return true;
    });
  }, [rows, search, due, type, appliance, owner, mineOnly, myEmail]);

  const boardPath = (name: string) =>
    `/${AnketaResourcesEnum.opsanio}/opl/${encodeURIComponent(name)}`;
  const openOnBoard = (row: OpenPointRow) =>
    navigate(`${boardPath(row.applianceName)}?item=${encodeURIComponent(row.id)}`);

  return (
    <EuiPage>
      <EuiPageBody>
        <EuiFlexGroup alignItems="flexEnd" gutterSize="m" responsive={false} wrap>
          <EuiFlexItem>
            <EuiTitle size="l">
              <h1>{t("overview.title", "Open points")}</h1>
            </EuiTitle>
            <EuiText size="s" color="subdued">
              {t(
                "overview.subtitle",
                "{{open}} open · {{overdue}} overdue · across {{appliances}} appliances",
                { open: counts.all, overdue: counts.overdue, appliances: appliances.length },
              )}
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton iconType="refresh" onClick={load} isLoading={loading}>
              {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="m" />

        {error && (
          <>
            <EuiCallOut
              color="danger"
              iconType="alert"
              title={t("overview.error", "The open points could not be loaded.")}
            >
              <p>{error}</p>
              <EuiButton size="s" color="danger" onClick={load}>
                {translate("anketa.common.retry", { defaultMessage: "Retry" })}
              </EuiButton>
            </EuiCallOut>
            <EuiSpacer size="m" />
          </>
        )}

        <EuiPanel hasBorder paddingSize="m">
          <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
            <EuiFlexItem grow={false} style={{ width: 280 }}>
              <EuiFieldSearch
                compressed
                fullWidth
                incremental
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("overview.search", "Search point, appliance, owner…")}
                aria-label={t("overview.search", "Search point, appliance, owner…")}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <DueFilterChips t={t} value={due} counts={counts} onChange={setDue} />
            </EuiFlexItem>
            <EuiFlexItem grow={true} />
            <EuiFlexItem grow={false} style={{ minWidth: 150 }}>
              <EuiSelect
                compressed
                aria-label={t("overview.column.type", "Type")}
                value={type}
                onChange={(e) => setType(e.target.value as OplItemType | "all")}
                options={[
                  { value: "all", text: t("overview.allTypes", "All types") },
                  { value: "A", text: t("type.A", "Task") },
                  { value: "E", text: t("type.E", "Decision") },
                  { value: "I", text: t("type.I", "Information") },
                ]}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false} style={{ minWidth: 180 }}>
              <EuiSelect
                compressed
                aria-label={t("overview.column.appliance", "Appliance")}
                value={appliance}
                onChange={(e) => setAppliance(e.target.value)}
                options={[
                  { value: "all", text: t("overview.allAppliances", "All appliances") },
                  ...appliances.map((a) => ({ value: a, text: a })),
                ]}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false} style={{ minWidth: 170 }}>
              <EuiSelect
                compressed
                aria-label={t("overview.column.owner", "Owner")}
                value={owner}
                disabled={mineOnly}
                onChange={(e) => setOwner(e.target.value)}
                options={[
                  { value: "all", text: t("overview.allOwners", "All owners") },
                  ...owners.map((o) => ({ value: o, text: o })),
                ]}
              />
            </EuiFlexItem>
            {myEmail && (
              <EuiFlexItem grow={false}>
                <EuiSwitch
                  compressed
                  label={t("overview.mine", "Only mine")}
                  checked={mineOnly}
                  onChange={(e) => setMineOnly(e.target.checked)}
                />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          <EuiSpacer size="s" />
          {loading && items === null ? (
            <TableSkeleton rows={8} />
          ) : (
            <OpenPointsTable
              t={t}
              rows={visible}
              loading={loading}
              onOpen={openOnBoard}
              onOpenAppliance={(name) => navigate(boardPath(name))}
              emptyMessage={
                rows.length === 0
                  ? t("overview.empty", "No open points - everything is done.")
                  : t("overview.noMatch", "No open points match the filters.")
              }
            />
          )}
        </EuiPanel>
      </EuiPageBody>
    </EuiPage>
  );
};
